import React from 'react';
import { observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import { StandardCard, Theme } from '@routific/hive';
import ColorBar from '../../shared/ColorBar';
import { TagsOverflowRow } from '../../shared/Tags';
import RouteSummaryProgressBar from '../RoutesPanel/RouteSummary/RouteSummaryProgressBar';
import MiniRouteInfo, { MiniRouteContainerType } from '../RouteInfo/MiniRouteInfo';
import RouteCardHeader from './RouteCardHeader';
import { RouteDtoModelType } from '../../../store/mst/models';

const ContentContainer = styled.div<{ $withTitle: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Theme.Spacing.smSpacing};
  ${({ $withTitle }) => $withTitle && css`margin-left: 12px;`}
  ${({ $withTitle }) => $withTitle && css`padding-left: 0;`}
  min-width: 0;
  width: 100%;
  height: max-content;
`;

interface Props {
  handleHoverCard: (hovered: boolean) => void;
  handleClickCard: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, uuid?: string, index?: number) => void;
  stateless?: boolean;
  isHovered: boolean;
  selected?: boolean;
  route: RouteDtoModelType;
  routeDetailsRef?: React.MutableRefObject<HTMLDivElement | null>;
  withTitle: boolean;
  withColorBar: boolean;
  pending: boolean;
  minutesLate: number | undefined;
  containerType: MiniRouteContainerType.BREAKDOWN | MiniRouteContainerType.OVERVIEW;
  withAssignDriver?: boolean;
  withStats?: boolean;
  showCheckbox?: boolean;
  isChecked?: boolean;
  onCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  routeUuid?: string;
}

const InnerRouteCard: React.FC<Props> = ({
  handleHoverCard,
  handleClickCard,
  stateless,
  isHovered,
  selected,
  route,
  routeDetailsRef,
  withTitle,
  withColorBar,
  pending,
  minutesLate,
  containerType,
  withAssignDriver,
  withStats,
  showCheckbox,
  isChecked,
  onCheckboxChange,
  routeUuid,
}) => {
  const getStandardCardStyle = () => {
    if (!withStats) {
      return {
        paddingLeft: '0px',
        marginBottom: `${Theme.Spacing.smSpacing}`,
      };
    }
    if (!stateless) {
      return {
        paddingLeft: '0',
      };
    }
    return {};
  };

  const getDivStyle = () => {
    if (!withStats) {
      return {
        display: 'flex',
        width: '100%',
        margin: '-1px',
      };
    }
    return {
      display: 'flex',
      width: '100%',
    };
  };

  return (
    <StandardCard
      onClick={handleClickCard}
      onMouseEnter={() => handleHoverCard(true)}
      onMouseLeave={() => handleHoverCard(false)}
      style={{
        ...(getStandardCardStyle()),
      }}
      stateless={stateless}
      isHovered={isHovered}
      selected={selected}
      data-selected={!!selected}
      data-cy-test-id={route.uuid}
      data-cy={route.isDispatched ? 'published-route-card' : 'route-card'}
    >
      <div style={getDivStyle()} ref={routeDetailsRef}>
        {withColorBar && <ColorBar loading={pending} color={route.color} />}
        <ContentContainer $withTitle={withTitle}>
          <RouteCardHeader
            route={route}
            isHovered={isHovered}
            withTitle={withTitle}
            pending={pending}
            withAssignDriver={withAssignDriver}
            withStats={withStats}
            showCheckbox={showCheckbox}
            isChecked={isChecked}
            onCheckboxChange={onCheckboxChange}
            routeUuid={routeUuid}
          />
          {!withTitle && route.tags && route.tags.length > 0 && (
            <TagsOverflowRow tags={route.tags.map((tag) => tag.name)} />
          )}
          {withStats && (
            <>
              <MiniRouteInfo
                type={containerType}
                route={route}
                large
              />
              <RouteSummaryProgressBar
                routeUuid={route.uuid}
                totalOrders={route.totalOrdersCount}
                published={route.isDispatched}
                percentProgress={route.percentProgress}
                delay={minutesLate}
              />
            </>
          )}
        </ContentContainer>
      </div>
    </StandardCard>
  );
};

export default observer(InnerRouteCard);
